import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BrokenIcon from 'src/assets/img/icons/link-broken.svg';
import ActivityFeedCmp from 'src/components/ActivityFeedCmp';
import ActivityFeedSkeleton from 'src/components/ActivityFeedCmp/ActivityFeedSkeleton';
import ButtonCmp from 'src/components/ButtonCmp';
import ToastBodyContainer from 'src/components/ToastContainer';
import { getBadgeLabelByQuoteStatus, getBadgeTypeByQuoteStatus, STATUS } from 'src/constants/common';
import { ROUTES } from 'src/constants/routes';
import { currentUser as currentUserSelector } from 'src/redux/auth.slice';
import { useAppSelector } from 'src/redux/hooks';
import {
    appendActivities,
    setActivities,
    setActivitiesLoading,
    setActivitiesPagination,
    setActiveQuotes as setActiveQuotesRedux,
    setActiveQuoteColumns,
    resetAllQuoteState,
} from 'src/redux/Quoting.slice';
import { setShareToken, getSharedQuote, getSharedActiveQuote, getSharedQuoteActivities } from 'src/services/ShareLinkService';

import { SelectQuoteBlock, TermsAndConditions } from '../MyQuote/Component';
import CustomerPortalStopMetaStrip from '../MyQuote/QuoteDetail/CustomerPortalStopMetaStrip';
import MapItWidget from '../MyQuote/QuoteDetail/MapIt';
import QuoteDetailMetaDataTable from '../MyQuote/QuoteDetail/QuoteDetailMetaDataTable';

import SharedMessagesView from './SharedMessagesView';
import SharedPageTopBar from './SharedPageTopBar';
import SharedQuoteHeader from './SharedQuoteHeader';

/**
 * SharedQuote — public, token-gated quote view.
 *
 * URL: /share-links/quote?t=<JWE>
 *
 * Renders the same screenshot UI as the auth QuoteDetail page (header summary,
 * Overview / Messages tabs, Select-a-Quote table, Route widget, Terms widget,
 * activity timeline) but:
 *   - Without the AppLayout shell (no top bar, no left sidebar)
 *   - Without sockets / write actions (composer hidden, Book Order + radio gone)
 *   - Without auth — the JWE in ?t= is the only authorization
 *
 * Reuses the auth ActivityFeedCmp for pixel-perfect parity with the logged-in
 * page. We feed activities into the shared Redux slice (same store the auth
 * page uses) and clear it on unmount so a logged-in user opening a share link
 * in another tab won't have their state polluted by the public flow.
 */

interface QuoteDetail {
    id?: number;
    code?: string | null;
    status?: string | null;
    pickupDate?: string | null;
    rossRocketPublicId?: string | null;
    shipperLatitude?: number | string | null;
    shipperLongitude?: number | string | null;
    shipperCity?: string | null;
    shipperStateCode?: string | null;
    shipperFullAddress?: string | null;
    shipperCountry?: string | null;
    consigneeLatitude?: number | string | null;
    consigneeLongitude?: number | string | null;
    consigneeCity?: string | null;
    consigneeStateCode?: string | null;
    consigneeFullAddress?: string | null;
    consigneeCountry?: string | null;
    // Server-derived: standard LTL (rates automatically) vs Volume LTL / FTL / Specialized (a sales
    // rep prices it). Picks the wording of the "Preparing your quote" block — same field and same
    // derivation the authenticated Quote Detail page uses.
    isInstantLtl?: boolean;
    tokenExpiresAt?: string | null;
    [key: string]: unknown;
}

type LoadState = 'loading' | 'success' | 'invalid-token' | 'expired' | 'not-found' | 'error';

/**
 * ErrorState — full-page error/empty card for share-link failure modes
 * (token expired, link invalid, quote not found, generic error).
 *
 * Matches the broken-link mock: centered broken-link SVG, underlined title,
 * subtext below. Icon is shared across all failure modes — the title/body
 * differentiate them.
 */
const ErrorState: React.FC<{ title: string; body: string }> = ({ title, body }) => (
    <div className="flex flex-col items-center justify-center text-center gap-5 max-w-[1480px] mx-auto rounded-xl w-full bg-white mt-4 max-lg:h-full max-lg:mb-4 lg:min-h-[500px]">
        <div className="flex flex-col items-center justify-center">
            <img src={BrokenIcon} alt="broken link" className="w-[80px] h-[80px] object-contain" />
            <div className="flex flex-col items-center gap-1.5 mt-5">
                <h1 className="text-[20px] font-semibold leading-[30px] text-grayLight900">{title}</h1>
                <p className="text-[14px] font-normal leading-[20px] text-gray500 max-lg:px-2">{body}</p>
            </div>
            <ButtonCmp className="btn-outline-primary mt-4" onClick={() => (window.location.href = ROUTES.HOME)}>
                Login to Portal
            </ButtonCmp>
        </div>
    </div>
);

const PageSkeleton: React.FC = () => (
    <div className="flex flex-col flex-1 min-h-0 w-full max-lg:overflow-auto scrollbar-hide">
        {/* Sticky header — mirrors the pt-4 space-y-4 block in success state */}
        <div className="pt-4 space-y-4 lg:pr-1">
            {/* Title row: code shimmer + copy icon + badge — reuses SharedQuoteHeader's own loading state */}
            <div className="flex items-center border-b border-borderPrimary pb-3">
                <SharedQuoteHeader isLoading={true} />
            </div>
            {/* Metadata bar: real column labels visible, values are shimmer rows */}
            <QuoteDetailMetaDataTable dataList={[]} isLoading={true} onOpenModal={() => {}} />
        </div>

        {/* Two-column body */}
        <div className="flex overflow-auto custom-scrollbar-main flex-1 max-lg:flex-col max-lg:overflow-visible">
            {/* Left column */}
            <div className="flex-1 max-lg:flex-none flex flex-col lg:overflow-hidden lg:pr-2 pt-4 lg:pt-5 min-w-0">
                <div className="flex-1 flex flex-col lg:-mx-2 lg:px-2 lg:overflow-auto custom-scrollbar-v2 lg:pb-4 space-y-4 lg:space-y-5">
                    {/* Select Quote table: real table header + shimmer carrier rows */}
                    <SelectQuoteBlock tableDataArr={[]} isLoading={true} isReadOnly />

                    {/* Activity feed skeleton — mirrors ActivityFeedCmp's own isReadOnly loading markup exactly, so mounting the real component causes no visual jump */}
                    <div className="flex flex-col overflow-auto flex-1 min-h-[400px]" data-testid="order-activity">
                        <div className="flex-1 flex flex-col bg-white border border-utilityGray200 rounded-xl shadow-xs py-6 overflow-auto scrollbar-hide px-0 !py-0 [&>*]:!px-4">
                            <div className="table-top-header mb-3">
                                <div className="table-left-wrap ">
                                    <div className="table-title-wrap">
                                        <h5 className="text-textSecondary text-sm font-semibold table-title">Latest Activity</h5>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col flex-1 overflow-auto custom-scrollbar-v2 px-4 lg:px-6 max-lg:max-h-[314px] lg:max-h-[calc(100vh-332px)]">
                                <ActivityFeedSkeleton />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right sidebar */}
            <div className="widget-right-sidebar-common-wrap max-lg:!w-full flex flex-col lg:overflow-auto custom-scrollbar-v2 pt-4 lg:pt-5">
                <div className="widget-right-sidebar-common-content widget-right-sidebar-common-content-x-spacing !mt-0 !pr-1 !pt-0 max-lg:!overflow-visible max-lg:!h-auto max-lg:!pl-0 max-lg:!pr-0">
                    {/* Map It: pulsing gray area with spinner — same as MapItWidget isLoading */}
                    <MapItWidget isLoading={true} title="Map It" showOpenButton={false} />

                    {/* Messages skeleton card */}
                    <div className="table-box-wrap">
                        <div className="table-top-header rounded-t-xl bg-gray25">
                            <div className="table-left-wrap">
                                <div className="table-title-wrap">
                                    <h5 className="table-title">Messages</h5>
                                </div>
                            </div>
                        </div>
                        <div className="p-4 h-[256px] lg:h-[192px] space-y-3 overflow-hidden">
                            {[...Array(3)].map((_, i) => (
                                <div key={i} className="flex gap-2">
                                    <div className="custom-loading w-7 h-7 rounded-full flex-none" />
                                    <div className="space-y-1.5 flex-1 pt-0.5">
                                        <div className="custom-loading h-3 w-1/3 rounded" />
                                        <div className="custom-loading h-3 w-2/3 rounded" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Terms & Conditions — static content, renders immediately */}
                    <TermsAndConditions />
                </div>
            </div>
        </div>
    </div>
);

const SharedQuote: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('t');
    const loggedInUser = useAppSelector(currentUserSelector);

    const [state, setState] = useState<LoadState>('loading');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [quote, setQuote] = useState<QuoteDetail | null>(null);
    const [activeQuotes, setActiveQuotes] = useState<any[]>([]);
    const [isLoadingActiveQuotes, setIsLoadingActiveQuotes] = useState<boolean>(true);
    const [activitiesNextCursor, setActivitiesNextCursor] = useState<number | null>(null);

    // Redirect logged-in users to the authenticated quote detail page.
    // Kept as a separate effect so it fires correctly even when auth rehydrates
    // after the quote data has already loaded (avoids stale closure on the fetch effect).
    useEffect(() => {
        if (loggedInUser && quote?.id) {
            navigate(`/my-quote/${quote.id}`, { replace: true });
        }
    }, [loggedInUser, quote, navigate]);

    // Initial activity fetch — populates the same Redux slice the auth page uses.
    // ActivityFeedCmp reads from there, so once we've dispatched, the component
    // renders with full parity (diff cards, integration cards, sentinel auto-load).
    const loadActivities = useCallback(
        async (lastId?: number | null, append = false) => {
            dispatch(setActivitiesLoading(true));

            try {
                const response: any = await getSharedQuoteActivities({ perPage: 25, ...(lastId ? { lastId } : {}) });
                const data = response?.data ?? response;
                const items = data?.activities ?? [];

                if (append) {
                    dispatch(appendActivities(items));
                } else {
                    dispatch(setActivities(items));
                }

                dispatch(
                    setActivitiesPagination(
                        data?.pagination
                            ? {
                                  hasMore: data.pagination.hasMore ?? false,
                                  nextCursor: data.pagination.nextCursor ?? null,
                                  totalItems: data.pagination.totalItems ?? 0,
                              }
                            : null
                    )
                );
                setActivitiesNextCursor(data?.pagination?.nextCursor ?? null);
            } catch {
                // No toast on public page — leave the empty state visible.
            } finally {
                dispatch(setActivitiesLoading(false));
            }
        },
        [dispatch]
    );

    const handleActivitiesLoadMore = useCallback(() => {
        if (activitiesNextCursor === null) return;
        loadActivities(activitiesNextCursor, true);
    }, [activitiesNextCursor, loadActivities]);

    useEffect(() => {
        if (!token) {
            setState('invalid-token');

            return;
        }

        setShareToken(token);
        let cancelled = false;

        (async () => {
            try {
                const [quoteResp, activeResp] = await Promise.allSettled([getSharedQuote(), getSharedActiveQuote()]);
                if (cancelled) return;

                if (quoteResp.status === 'rejected') {
                    const err: any = quoteResp.reason;
                    const status = err?.response?.status;

                    if (status === 401) setState('expired');
                    else if (status === 404) setState('not-found');
                    else {
                        setErrorMessage(err?.response?.data?.message || err?.message || 'Failed to load quote');
                        setState('error');
                    }

                    return;
                }

                const quoteData: QuoteDetail = (quoteResp.value as any)?.data ?? quoteResp.value;
                setQuote(quoteData);
                setState('success');

                if (activeResp.status === 'fulfilled') {
                    const activeData: any = (activeResp.value as any)?.data ?? activeResp.value;
                    const list = Array.isArray(activeData?.activeQuote) ? activeData.activeQuote : [];
                    setActiveQuotes(list);
                    // ActivityFeedCmp + SelectQuoteTableRow read quoteColumns from
                    // Redux — push the BE-resolved column visibility there too so
                    // hidden columns honor the same config as the auth page.
                    dispatch(setActiveQuotesRedux(list));
                    dispatch(setActiveQuoteColumns(activeData?.quoteColumns ?? null));
                }
                setIsLoadingActiveQuotes(false);

                // Activities load once the quote has been verified — anything
                // earlier risks a flash of "no activity" while still authenticating.
                loadActivities();
            } catch (error: any) {
                if (cancelled) return;
                setErrorMessage(error?.response?.data?.message || error?.message || 'Failed to load quote');
                setState('error');
            }
        })();

        return () => {
            cancelled = true;
            setShareToken(null);
            // Clear the shared Redux slice so a logged-in user in another tab
            // doesn't see this share link's quote/activities/columns in their
            // own session after this page unmounts.
            dispatch(resetAllQuoteState());
            dispatch(setActivities([]));
            dispatch(setActivitiesPagination(null));
        };
    }, [token, dispatch, loadActivities]);

    const mapData = useMemo(() => {
        if (!quote) return null;
        const shipperLat = quote.shipperLatitude;
        const shipperLng = quote.shipperLongitude;
        const consigneeLat = quote.consigneeLatitude;
        const consigneeLng = quote.consigneeLongitude;

        const startName = quote.shipperCity && quote.shipperStateCode ? `${quote.shipperCity}, ${quote.shipperStateCode}` : quote.shipperFullAddress || null;
        const endName = quote.consigneeCity && quote.consigneeStateCode ? `${quote.consigneeCity}, ${quote.consigneeStateCode}` : quote.consigneeFullAddress || null;

        const startPoint =
            shipperLat && shipperLng
                ? {
                      lat: typeof shipperLat === 'string' ? parseFloat(shipperLat) : (shipperLat as number),
                      lng: typeof shipperLng === 'string' ? parseFloat(shipperLng) : (shipperLng as number),
                      name: startName,
                  }
                : null;

        const endPoint =
            consigneeLat && consigneeLng
                ? {
                      lat: typeof consigneeLat === 'string' ? parseFloat(consigneeLat) : (consigneeLat as number),
                      lng: typeof consigneeLng === 'string' ? parseFloat(consigneeLng) : (consigneeLng as number),
                      name: endName,
                  }
                : null;

        const preventCrossBorder = !!(quote.shipperCountry && quote.consigneeCountry && quote.shipperCountry === quote.consigneeCountry);

        return {
            startPoint,
            endPoint,
            waypoints: [] as any[],
            currentLocation: null,
            preventCrossBorder,
            statusInfo: quote.status
                ? {
                      status: getBadgeLabelByQuoteStatus(quote.status),
                      statusType: getBadgeTypeByQuoteStatus(quote.status),
                  }
                : undefined,
        };
    }, [quote]);

    return (
        <main className="h-screen flex bg-utilityBlack700 flex-col w-full">
            <ToastBodyContainer />
            <SharedPageTopBar />

            <div className="flex-1 flex flex-col min-h-0 lg:overflow-hidden w-full mx-auto px-2 lg:px-4 rounded-t-xl global-background-color">
                <div className="flex flex-col flex-1 min-h-0 max-w-[1480px] w-full mx-auto px-2">
                    {state === 'loading' && <PageSkeleton />}
                    {state === 'invalid-token' && <ErrorState title={t('SharedQuote.Invalid_link_title')} body={t('SharedQuote.Invalid_link_body')} />}
                    {state === 'expired' && <ErrorState title={t('SharedQuote.Expired_title')} body={t('SharedQuote.Expired_body')} />}
                    {state === 'not-found' && <ErrorState title={t('SharedQuote.Not_found_title')} body={t('SharedQuote.Not_found_body')} />}
                    {state === 'error' && <ErrorState title={t('SharedQuote.Error_title')} body={errorMessage || t('SharedQuote.Error_body')} />}

                    {state === 'success' && quote && (
                        <div className="flex flex-col flex-1 min-h-0 w-full max-lg:overflow-auto scrollbar-hide max-lg:pb-[60px]">
                            {/* Title row + metadata — sticky on mobile so header stays visible while content scrolls */}
                            <div className="pt-4 space-y-4 lg:pr-1 lg:sticky lg:top-0 lg:z-20 global-background-color">
                                <div
                                    className="flex lg:items-center lg:flex-row flex-row justify-between border-b border-borderPrimary pb-3
					xls:!flex-nowrap !flex-wrap mdm:!flex-row md:!flex-col sm:!flex-row mdm:!items-center !items-start"
                                >
                                    <SharedQuoteHeader code={quote.code} status={quote.status} hasActiveQuotes={activeQuotes.length > 0} isLoading={false} />
                                </div>

                                <QuoteDetailMetaDataTable
                                    dataList={quote?.id ? [quote] : []}
                                    isLoading={false}
                                    onOpenModal={() => {
                                        /* read-only: no edit modal in public view */
                                    }}
                                />
                            </div>

                            {/* Two-column area — desktop: independent column scroll; mobile: natural flow under sticky header */}
                            <div className="flex lg:overflow-auto custom-scrollbar-main flex-1 max-lg:flex-col max-lg:overflow-visible">
                                {/* Left column */}
                                <div className="flex-1 max-lg:flex-none flex flex-col lg:overflow-hidden lg:pr-2 pt-4 lg:pt-5 min-w-0">
                                    <div className="flex-1 flex flex-col lg:-mx-2 lg:px-2 lg:overflow-auto custom-scrollbar-v2 lg:pb-4 space-y-4 lg:space-y-5">
                                        {/* <div className="sticky top-0 z-[99] global-background-color">
                                            <TabButton
                                                parentClassName="tab-border-bottom-parent border-b !border-borderPrimary w-full"
                                                activeClassName="tab-border-bottom-active"
                                                tabParentClassName="px-1 -mx-1 global-background-color"
                                                className="tab-border-bottom flex-1 max-w-[200px] !pt-2.5 lg:!pt-[6px]"
                                                tabBadgeClassName="tab-border-bottom-badge"
                                                tabArray={[{ value: 'overview', name: 'Overview' }]}
                                                isActive="overview"
                                                handleOnClick={() => {}}
                                            />
                                        </div> */}
                                        {/* Multi-stop route summary — same component and same
                                            length > 2 guard as the authenticated Quote Detail page
                                            (the bookends are always present, so > 2 means at least
                                            one genuine customer-visible middle stop survived). */}
                                        {(quote.stops as any[])?.length > 2 && (
                                            <CustomerPortalStopMetaStrip
                                                stops={quote.stops as any}
                                                summary={quote.multiStopSummary as any}
                                                dimensions={quote.dimensions as any}
                                                selectedServices={quote.selectedServices as any}
                                            />
                                        )}
                                        <SelectQuoteBlock
                                            tableDataArr={activeQuotes}
                                            isLoading={isLoadingActiveQuotes}
                                            isToBookStatus={activeQuotes.some((q: any) => q.status === STATUS.TO_BOOK || q.status === STATUS.WON)}
                                            pickupDate={quote.pickupDate ?? undefined}
                                            // A shared quote with no rates yet shows the same "Preparing your quote" block as
                                            // the authenticated page, so it needs the same Instant-LTL vs sales-rep verdict.
                                            // Only that field: the share view has no lane/distance data (no Mapbox Directions
                                            // lookup on a public page), so the Lane Intelligence panel degrades exactly as it
                                            // does today rather than gaining cards built from half the inputs.
                                            laneContext={{ isInstantLtl: quote.isInstantLtl }}
                                            isReadOnly
                                        />
                                        {quote.id && <ActivityFeedCmp entityType="quote" entityId={quote.id} handleLoadMore={handleActivitiesLoadMore} wrapperClassName="min-h-[360px]" isReadOnly />}
                                    </div>
                                </div>

                                {/* Right sidebar widgets */}
                                <div className="widget-right-sidebar-common-wrap max-lg:!w-full flex flex-col lg:overflow-auto custom-scrollbar-v2 space-y-4 pt-4 lg:pt-5 lg:space-y-5">
                                    {/* <div className="widget-right-sidebar-common-content-x-spacing max-lg:!pl-1 !pr-1">
                                        <div className="border-b border-borderPrimary w-full pb-1.5 flex items-center min-h-[35px]">
                                            <h6 className="text-textSecondary text-sm font-semibold">Widgets</h6>
                                        </div>
                                    </div> */}
                                    <div className="widget-right-sidebar-common-content widget-right-sidebar-common-content-x-spacing !mt-0 !pr-1 max-lg:!overflow-visible max-lg:!h-auto max-lg:!pl-0 !pt-0 max-lg:!pr-0 max-lg:!pb-8">
                                        {mapData && (
                                            <MapItWidget
                                                startPoint={mapData.startPoint}
                                                endPoint={mapData.endPoint}
                                                waypoints={mapData.waypoints}
                                                currentLocation={mapData.currentLocation}
                                                title={t('QuotingHub.PredictivePricing.Map-It')}
                                                preventCrossBorder={mapData.preventCrossBorder}
                                                showOpenButton={false}
                                                statusInfo={mapData.statusInfo as any}
                                                isLoading={false}
                                            />
                                        )}
                                        <SharedMessagesView quoteId={quote.id} />
                                        <TermsAndConditions />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </main>
    );
};

export default SharedQuote;
